import React, { useState, useEffect, ChangeEvent, Fragment } from 'react';
import {
    Box,
    Button,
    Divider,
    Input,
    Paper,
    Portal,
    makeStyles,
    Fade,
    Theme,
    ButtonProps,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import useDocument from 'src/hooks/useDocument';
import { IModalMail, TMail } from './types';
import { Header } from './Header';
import { DocumentList } from './DocumentList';
import { IDocument } from 'src/interfaces/document';
import { AWS_S3 } from 'src/constants/crm/aws';
import { getMailBody } from '../utils/getMailBody';
import { DOCUMENTATION_SUBJECT } from '../constants';
import SITE_SETTINGS from 'src/constants/whiteLabel';
import { CONTAINED, PRIMARY } from 'src/constants/variants/variants';
import { Dropzone } from 'src/components/Dropzone';
import { AttachmentList } from './AttachmentList';
import useLead from 'src/hooks/useLead';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        maxWidth: `calc(100% - ${theme.spacing(10)}px)`,
        maxHeight: `calc(100% - ${theme.spacing(10)}px)`,
        width: 600,
        position: 'fixed',
        bottom: 0,
        right: 0,
        margin: theme.spacing(3),
        outline: 'none',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '40vh',
    },
    message: {
        padding: 15,
        paddingTop: 0,
        minHeight: 150,
    },
    dropzoneButton: {
        marginRight: 20,
    },
}));

export const ModalMail = ({ lead, vehicle, setOpen, ...props }: IModalMail) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { sendDocumentationV2 } = useLead();
    const [files, setFiles] = useState<File[]>([]);

    const { subject, message } = getMailBody(lead, vehicle, SITE_SETTINGS.client);

    const [mail, setMail] = useState<TMail>({
        template: `documentation_${SITE_SETTINGS.client}`,
        subject,
        message,
        email: lead?.email || '',
        store:
            typeof lead?.store === 'string'
                ? lead?.store
                : lead?.store?._id || '',
        user: lead?.agent?._id || '',
        lead: lead?._id,
        documents: [],
        attachments: [],
    });

    const { getDocumentsLead, documents } = useDocument();

    const handleChange = (event: ChangeEvent<HTMLInputElement>) =>
        setMail({ ...mail, [event.target.name]: event.target.value });

    const handleSubmit = async () => {
        sendDocumentationV2(mail);
        setFiles([]);
        setOpen(false);
    };

    const handleAddFile = (files: File[]) =>
        setFiles((prev) => [...prev, ...files]);

    useEffect(() => {
        if (lead._id) getDocumentsLead(lead._id);
    }, [lead]);

    const handleSetDocuments = (documents: IDocument[]) => {
        const auxiliar = documents.map((item) => {
            return {
                title: item.title,
                file: AWS_S3 + item.file,
                id: item._id,
            };
        });

        setMail({ ...mail, documents: auxiliar });
    };

    const handleSetAttachments = (attachments: File[]) =>
        setMail({ ...mail, attachments });

    useEffect(() => {
        if (documents) handleSetDocuments(documents);
    }, [documents]);

    useEffect(() => {
        if (files) handleSetAttachments(files);
    }, [files]);

    const BUTTON_PROPS: ButtonProps = {
        variant: CONTAINED,
        color: PRIMARY,
    };

    return (
        <Portal {...props}>
            <Fade in timeout={600}>
                <Paper className={classes.root} elevation={1}>
                    <Header setOpen={setOpen} />
                    <Box p={2}>
                        <Input
                            value={`${t('Email.To')}: ${lead.email}`}
                            disableUnderline
                            disabled
                            fullWidth
                        />
                    </Box>
                    <Divider />
                    <DocumentList
                        setMail={setMail}
                        mail={mail}
                        documents={documents}
                    />
                    {mail.attachments && mail.attachments.length > 0 && (
                        <Fragment>
                            <Divider />
                            <AttachmentList
                                attachments={mail.attachments}
                                setFiles={setFiles}
                                files={files}
                            />
                        </Fragment>
                    )}
                    <Divider />
                    <Input
                        placeholder={t('Email.Message')}
                        className={classes.message}
                        onChange={handleChange}
                        value={mail.message}
                        disableUnderline
                        name="message"
                        multiline
                        fullWidth
                    />
                    <Divider />
                    <Box display="flex" alignItems="center" py={1} px={2}>
                        <Dropzone
                            multiple
                            className={classes.dropzoneButton}
                            handleAdd={handleAddFile}
                        >
                            <Button {...BUTTON_PROPS}>
                                {t('Documents.Select')}
                            </Button>
                        </Dropzone>
                        <Button {...BUTTON_PROPS} onClick={handleSubmit}>
                            {t('Buttons.Send')}
                        </Button>
                    </Box>
                </Paper>
            </Fade>
        </Portal>
    );
};
